import gql from 'graphql-tag';
import * as React from 'react';
import { useQuery, useMutation } from 'react-apollo';
import { RouteComponentProps } from 'react-router';
import { RemoveUser, RemoveUserVariables } from './__generated__/RemoveUser';
import { ReSendInvite, ReSendInviteVariables } from './__generated__/ReSendInvite';
import { SendInvite, SendInviteVariables } from './__generated__/SendInvite';
import { UpdateInvite, UpdateInviteVariables } from './__generated__/UpdateInvite';
import { UpdateRole, UpdateRoleVariables } from './__generated__/UpdateRole';
import { UserListQuery, UserListQueryVariables } from './__generated__/UserListQuery';
import { Management } from './Management';
import { useQueryParams } from '../../../hooks';

const QUERY = gql`
  query UserListQuery($venueId: ID!, $page: Int!, $role: String, $search: String) {
    users(venueId: $venueId, page: $page, role: $role, search: $search) {
      id
      email
      firstName
      lastName
      inviteAccepted
      role {
        id
        name
      }
      status
    }
    roles {
      id
      name
      permissions
    }
  }
`;

const SEND_INVITE = gql`
  mutation SendInvite($invite: InviteInput!) {
    sendInvite(invite: $invite)
  }
`;

const RESEND_INVITE = gql`
  mutation ReSendInvite($inviteId: ID!) {
    resendInvite(inviteId: $inviteId)
  }
`;

const UPDATE_ROLE = gql`
  mutation UpdateRole($userId: ID!, $roleId: ID!) {
    updateRole(userId: $userId, roleId: $roleId)
  }
`;

const UPDATE_INVITE = gql`
  mutation UpdateInvite($inviteId: ID!, $roleId: ID!) {
    updateInvite(inviteId: $inviteId, roleId: $roleId)
  }
`;

const REMOVE_USER = gql`
  mutation RemoveUser($userId: ID!) {
    removeUser(userId: $userId)
  }
`;

interface ManagementRouterParams {
  venueId: string;
}

type ManagementProps = RouteComponentProps<ManagementRouterParams>;

export const ManagementContainer: React.FC<ManagementProps> = (props) => {
  const queryParams = useQueryParams();
  const variables: UserListQueryVariables = {
    page: queryParams.get('page') ? Number(queryParams.get('page')) : 1,
    role: queryParams.get('role'),
    search: queryParams.get('search'),
    venueId: props.match.params.venueId,
  };

  const [updateInvite] = useMutation<UpdateInvite, UpdateInviteVariables>(UPDATE_INVITE);
  const [resendInvite] = useMutation<ReSendInvite, ReSendInviteVariables>(RESEND_INVITE);
  const [removeUser] = useMutation<RemoveUser, RemoveUserVariables>(REMOVE_USER);
  const [updateRole] = useMutation<UpdateRole, UpdateRoleVariables>(UPDATE_ROLE);
  const [sendInvite] = useMutation<SendInvite, SendInviteVariables>(SEND_INVITE);

  const { data, loading, refetch, error } = useQuery<UserListQuery, UserListQueryVariables>(QUERY, {
    variables,
  });

  return (
    <Management
      error={error}
      data={data}
      loading={loading}
      sendInvite={sendInvite}
      updateRole={updateRole}
      removeUser={removeUser}
      refetch={refetch}
      variables={variables}
      resendInvite={resendInvite}
      updateInvite={updateInvite}
      {...props}
    />
  );
};
